import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { QUESTION_USAGES } from "../../api/assessmentQuestions";
import {
  TEST_QUESTION_PURPOSES,
  listTestTypesPage,
  createTestType,
  updateTestType,
  deleteTestType,
  apiErrorMessage,
} from "../../api/assessmentTests";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import useColumnVisibility from "./useColumnVisibility";
import ColumnsMenuButton from "./ColumnsMenuButton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

// A test type may also accept both usages ("any"), which the API stores as a null question usage.
const TEST_TYPE_USAGES = [{ value: "ANY", label: "Any (New and PYQ)" }, ...QUESTION_USAGES];

// All test types share one distribution level, so the form no longer offers a choice.
const ALL_TYPES_LEVEL = "TOPIC_SUBTOPIC";

function DialogHeader({ title, subtitle, onClose }) {
  return (
    <Box
      sx={{
        position: "relative",
        p: 2.5,
        background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
        color: "#fff",
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.18)",
          }}
        >
          <CategoryOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>{title}</Typography>
          <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

DialogHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

function TestTypeFormDialog({ open, testType, onClose, onSave }) {
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [questionUsage, setQuestionUsage] = useState("NEW"); // NEW, PYQ or ANY (stored as null)
  const [questionPurposes, setQuestionPurposes] = useState([]);
  const [active, setActive] = useState(true);
  const [allowMultipleChapters, setAllowMultipleChapters] = useState(false);
  const [minPyqPercentage, setMinPyqPercentage] = useState(""); // blank / 0 = no minimum
  const [practiceMaxQuestions, setPracticeMaxQuestions] = useState(""); // blank / 0 = no cap
  const [practiceSecondsPerQuestion, setPracticeSecondsPerQuestion] = useState(""); // blank / 0 = default (60)
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setName(testType?.name || "");
    setQuestionUsage(testType ? testType.questionUsage || "ANY" : "NEW");
    setQuestionPurposes(testType?.questionPurposes || []);
    setActive(testType ? Boolean(testType.active) : true);
    setAllowMultipleChapters(Boolean(testType?.allowMultipleChapters));
    setMinPyqPercentage(testType?.minPyqPercentage ? String(testType.minPyqPercentage) : "");
    setPracticeMaxQuestions(testType?.practiceMaxQuestions ? String(testType.practiceMaxQuestions) : "");
    setPracticeSecondsPerQuestion(
      testType?.practiceSecondsPerQuestion ? String(testType.practiceSecondsPerQuestion) : "",
    );
  }, [open, testType]);

  // The minimum PYQ share only applies when the type accepts both usages
  const pyqShareApplies = questionUsage === "ANY";
  const pyqShareValid =
    !pyqShareApplies ||
    minPyqPercentage === "" ||
    (Number.isInteger(Number(minPyqPercentage)) && Number(minPyqPercentage) >= 0 && Number(minPyqPercentage) <= 100);
  // The question cap is only read for the seeded "Practice" type (auto-generated practice tests)
  const practiceCapApplies = name.trim().toLowerCase() === "practice";
  const practiceCapValid =
    !practiceCapApplies ||
    practiceMaxQuestions === "" ||
    (Number.isInteger(Number(practiceMaxQuestions)) && Number(practiceMaxQuestions) >= 0);
  const practiceSecondsValid =
    !practiceCapApplies ||
    practiceSecondsPerQuestion === "" ||
    (Number.isInteger(Number(practiceSecondsPerQuestion)) && Number(practiceSecondsPerQuestion) >= 0);
  const isValid = name.trim().length > 0 && pyqShareValid && practiceCapValid && practiceSecondsValid;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: testType?.id,
        name: name.trim(),
        distributionLevel: ALL_TYPES_LEVEL,
        ...(questionUsage === "ANY" ? { anyQuestionUsage: true } : { questionUsage }),
        questionPurposes,
        allowMultipleChapters,
        // 0 clears the requirement (also sent when the usage is not "Any", where it has no meaning)
        minPyqPercentage: pyqShareApplies ? Number(minPyqPercentage) || 0 : 0,
        // 0 clears the cap; omitted for other types so their stored value is left untouched
        ...(practiceCapApplies
          ? {
              practiceMaxQuestions: Number(practiceMaxQuestions) || 0,
              practiceSecondsPerQuestion: Number(practiceSecondsPerQuestion) || 0,
            }
          : {}),
        active,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <DialogHeader
        title={testType ? "Edit Test Type" : "Add Test Type"}
        subtitle={testType ? "Update name, question usage and status" : "Name it and set how its questions are used"}
        onClose={onClose}
      />
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Stack spacing={2.25} sx={{ pt: 0.5 }}>
          <TextField
            label="Test Type Name"
            fullWidth
            size="small"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && !name.trim()}
            helperText={nameTouched && !name.trim() ? "Please fill out this field" : undefined}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Question Usage</InputLabel>
            <Select label="Question Usage" value={questionUsage} onChange={(e) => setQuestionUsage(e.target.value)}>
              {TEST_TYPE_USAGES.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {u.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {pyqShareApplies && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Minimum PYQ questions (%)"
              value={minPyqPercentage}
              onChange={(e) => setMinPyqPercentage(e.target.value)}
              error={!pyqShareValid}
              helperText={
                pyqShareValid
                  ? "At least this share of every test of this type comes from PYQ. Blank or 0 = no minimum."
                  : "Whole number from 0 to 100"
              }
              inputProps={{ min: 0, max: 100, step: 1 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}
          {practiceCapApplies && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max questions per practice test"
              value={practiceMaxQuestions}
              onChange={(e) => setPracticeMaxQuestions(e.target.value)}
              error={!practiceCapValid}
              helperText={
                practiceCapValid
                  ? "Cap on newly generated practice tests. Blank or 0 = no cap (all eligible questions)."
                  : "Whole number, 0 or more"
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}
          {practiceCapApplies && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Seconds per question"
              value={practiceSecondsPerQuestion}
              onChange={(e) => setPracticeSecondsPerQuestion(e.target.value)}
              error={!practiceSecondsValid}
              helperText={
                practiceSecondsValid
                  ? "Time allotted per question of newly generated practice tests. Blank or 0 = default (60)."
                  : "Whole number, 0 or more"
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Question Purposes (blank = any)</InputLabel>
            <Select
              multiple
              label="Question Purposes (blank = any)"
              value={questionPurposes}
              onChange={(e) => setQuestionPurposes(e.target.value)}
              renderValue={(selected) =>
                selected.map((v) => TEST_QUESTION_PURPOSES.find((p) => p.value === v)?.label || v).join(", ")
              }
            >
              {TEST_QUESTION_PURPOSES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  <Checkbox size="small" checked={questionPurposes.includes(p.value)} sx={{ mr: 1, p: 0.5 }} />
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch checked={allowMultipleChapters} onChange={(e) => setAllowMultipleChapters(e.target.checked)} />
            }
            label="Allow multiple subjects and chapters in one test (Mock / PYQ)"
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label={active ? "Active" : "Inactive"}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: "linear-gradient(135deg, #123a80 0%, #0d47a1 100%)" },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {testType ? "Save Changes" : "Create Test Type"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

TestTypeFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  testType: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const headCellSx = {
  padding: "10px 12px",
  backgroundColor: ACCENT,
  color: "white",
  fontWeight: 600,
};

// Lists / adds / edits / deactivates test types (Content tab).
const allColumns = [
  { key: "id", label: "ID", sx: { minWidth: 50, width: 50 } },
  { key: "name", label: "Name", sx: { minWidth: 130, width: 130 } },
  { key: "usage", label: "Question Usage", sx: { minWidth: 100 } },
  { key: "purposes", label: "Question Purposes", sx: { minWidth: 150 } },
  { key: "multiChapters", label: "Multiple Chapters", align: "center", sx: { minWidth: 90 } },
  { key: "minPyq", label: "Min PYQ %", align: "center", sx: { minWidth: 70 } },
  { key: "maxQuestions", label: "Max Questions", align: "center", sx: { minWidth: 80 } },
  { key: "seconds", label: "Seconds / Question", align: "center", sx: { minWidth: 90 } },
  { key: "status", label: "Status", align: "center", sx: { minWidth: 85 } },
];

const renderCell = (key, t) => {
  switch (key) {
    case "id":
      return <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{t.id}</Typography>;
    case "name":
      return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{t.name}</Typography>;
    case "usage":
      return TEST_TYPE_USAGES.find((u) => u.value === (t.questionUsage || "ANY"))?.label ?? "—";
    case "purposes":
      return t.questionPurposes?.length
        ? t.questionPurposes.map((p) => TEST_QUESTION_PURPOSES.find((q) => q.value === p)?.label ?? p).join(", ")
        : "—";
    case "multiChapters":
      return t.allowMultipleChapters ? "Yes" : "No";
    case "minPyq":
      return t.minPyqPercentage ? `${t.minPyqPercentage}%` : "—";
    case "maxQuestions":
      return t.practiceMaxQuestions || "—";
    case "seconds":
      return t.practiceSecondsPerQuestion || "—";
    case "status":
      return (
        <Chip
          label={t.active ? "Active" : "Inactive"}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.7rem",
            color: t.active ? "#2e7d32" : "#555",
            borderColor: t.active ? "#2e7d32" : "#555",
            backgroundColor: t.active ? "#e8f5e9" : "#f0f0f0",
          }}
        />
      );
    default:
      return null;
  }
};

function AssessmentTestTypes() {
  const { visibleColumns, toggleColumn, visibleColumnDefs } = useColumnVisibility(
    "assessmentTestTypesHiddenColumns",
    allColumns,
  );
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const requestSeq = useRef(0);

  const loadTypes = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    listTestTypesPage({ page, size: rowsPerPage })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        // Deleting the last row of the last page leaves it empty: step back one page.
        if (data.content.length === 0 && page > 0 && data.totalPages > 0) {
          setPage(data.totalPages - 1);
          return;
        }
        setTypes(data.content);
        setTotalElements(data.totalElements ?? data.content.length);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setTypes([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load test types"));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (type) => {
    setEditing(type);
    setFormOpen(true);
  };

  const handleSave = ({ id, ...payload }) => {
    const request = id ? updateTestType(id, payload) : createTestType(payload);
    return request
      .then(() => {
        toast.success(id ? "Test type updated successfully!" : "Test type created successfully!");
        setFormOpen(false);
        loadTypes();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save test type"));
      });
  };

  const handleDelete = () => {
    deleteTestType(deleteId)
      .then(() => {
        toast.success("Test type deactivated!");
        setDeleteId(null);
        loadTypes();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to delete test type"));
      });
  };

  return (
    <>
      <Box sx={{ textAlign: "left" }}>
        <ToastContainer />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
            Manage the types a test can belong to
          </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <ColumnsMenuButton allColumns={allColumns} visibleColumns={visibleColumns} onToggle={toggleColumn} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Test Type
              </Button>
            </Box>
          </Box>
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 260px)", minHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {visibleColumnDefs.map((c) => (
                    <TableCell key={c.key} align={c.align} sx={{ ...headCellSx, ...c.sx }}>
                      {c.label}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ ...headCellSx, minWidth: 90 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
                ) : types.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                      <AssessmentEmptyState message="No test types found" />
                    </TableCell>
                  </TableRow>
                ) : (
                  types.map((t) => (
                    <TableRow key={t.id} hover>
                      {visibleColumnDefs.map((c) => (
                        <TableCell
                          key={c.key}
                          align={c.align}
                          sx={{ padding: "10px 12px", textAlign: c.align ?? "left", ...c.sx }}
                        >
                          {renderCell(c.key, t)}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit Test Type" arrow placement="top">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEdit(t)}
                              sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate Test Type" arrow placement="top">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(t.id)}
                              sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
            }}
          />
          </Paper>
      </Box>

      <TestTypeFormDialog
        open={formOpen}
        testType={editing}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="This deactivates the test type and hides it from new tests. Existing tests keep their type."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}

export default AssessmentTestTypes;
